import { FFmpeg } from '@ffmpeg/ffmpeg'
import { fetchFile, toBlobURL } from '@ffmpeg/util'
import { getFileType } from '../file'
import { assetsUpload } from '../upload'

// ffmpeg实例
let ffmpegInstance: FFmpeg | null = null

// https://api.gatherstar.io/front-resource/ffmpeg-core/0.12.10/core/ffmpeg-core.js
// 设置地址
const baseURL = 'https://api.gatherstar.io/front-resource/ffmpeg-core/0.12.10'
/**
 * @description: 初始化 FFmpeg
 * @return {*}
 */
const getFFmpeg = async () => {
    if (ffmpegInstance) {
        return ffmpegInstance
    } else {
        ffmpegInstance = new FFmpeg()
    }
    // 开发环境开启日志
    if (process.env.GLOBAL_ENV === 'dev') {
        ffmpegInstance.on('log', ({ message }) => {
            console.log(message)
        })
    }
    await ffmpegInstance.load({
        coreURL: await toBlobURL(
            `${baseURL}/core/ffmpeg-core.js`,
            'text/javascript',
        ),
        wasmURL: await toBlobURL(
            `${baseURL}/core/ffmpeg-core.wasm`,
            'application/wasm',
        ),
        classWorkerURL: await toBlobURL(
            `${baseURL}/worker/worker.js`,
            'text/javascript',
        ),
    })
    return ffmpegInstance
}

/**
 * @description: 获取写入ffmpeg的文件名称，后面拼上原fileName（写入时必须调用避免并发问题）
 * @param {string} fileName/文件名称
 * @param {string} isNew/是否重新生成文件名（不携带原fileName）
 * @return {*}
 */
const getFileName = (fileName: string, isNew = false) => {
    return (
        Math.random().toString(36).slice(2) +
        (isNew ? `.${getFileType(fileName)}` : fileName)
    )
}

/**
 * @description: 执行ffmpeg命令
 * @param {string} sh
 * @return {*}
 */
const run = async (sh: string) => {
    const ffmpeg = await getFFmpeg()
    const shArr = sh.split(' ')
    if (!shArr.length) {
        return Promise.reject('sh is empty')
    }
    const [exec, ...arg] = shArr
    if (exec === 'ffprobe') {
        await ffmpeg.ffprobe(arg)
    }
    if (exec === 'ffmpeg') {
        await ffmpeg.exec(arg)
    }
    return ffmpeg
}

/**
 * @description: 向ffmpeg写入文件
 * @param {File} inputFile
 * @return {*}
 */
const writeFile = async (inputFile: File) => {
    // 重新生成名称避免奇怪字符
    const inputFileName = getFileName(inputFile.name, true)
    const ffmpeg = await getFFmpeg()
    await ffmpeg.writeFile(inputFileName, await fetchFile(inputFile))
    return inputFileName
}

// 将 H.265 编码的 MP4 视频转换为 H.264 编码
// export const convertH265ToH264 = async (inputFile: File): Promise<Blob> => {
//     await init(inputFile);

//     // 将输入文件写入 FFmpeg 虚拟文件系统
//     const inputArrayBuffer = await inputFile.arrayBuffer();
//     await ffmpeg.writeFile(inputFile.name, new Uint8Array(inputArrayBuffer));

//     // 构建输出文件名
//     const inputFileName = inputFile.name;
//     const outputFileName = inputFileName.replace(/\.[^/.]+$/, '') + '_h264.mp4';

//     // 执行转换命令，将 H.265 转换为 H.264
//     // 使用 libx264 编码器进行转换
//     const result = await ffmpeg.exec([
//         '-i', inputFileName,           // 输入文件
//         '-c:v', 'libx264',            // 视频编码器使用 H.264
//         '-preset', 'medium',          // 编码速度与质量平衡
//         '-crf', '23',                 // 视频质量控制，值越小质量越高
//         '-c:a', 'aac',                // 音频编码器
//         '-strict', 'experimental',    // 启用实验性功能
//         outputFileName                 // 输出文件
//     ]);

//     if (result !== 0) {
//         throw new Error(`FFmpeg 转换失败，返回码: ${result}`);
//     }

//     // 从 FFmpeg 虚拟文件系统读取输出文件
//     const data = await ffmpeg.readFile(outputFileName);
//     return new Blob([data], { type: 'video/mp4' });
// };

// 计算分数表达式
const calculateFraction = (fractionStr: string): number => {
    if (!fractionStr || fractionStr === 'N/A') {
        return 0
    }

    // 检查是否为分数格式 (如 "24000/1001")
    if (fractionStr.includes('/')) {
        const [numerator, denominator] = fractionStr.split('/').map(Number)
        if (denominator && !isNaN(numerator) && !isNaN(denominator)) {
            return numerator / denominator
        }
    } else if (!isNaN(Number(fractionStr))) {
        // 如果是纯数字，直接返回
        return Number(fractionStr)
    }

    return 0
}

/**
 * @description: // 获取视频详细信息（编码格式、码率等）
 * @param {File} inputFile
 * @return {*}
 */
export const getVideoInfo = async (
    inputFile: File,
): Promise<{
    // 文件名
    fileName: string
    // 视频编码格式 (如 h264, hevc 等)
    videoCodec: string
    // 总码率 (bps)
    bitrate: number
    // 时长 (秒)
    duration: number
    // 视频宽度
    width: number
    // 视频高度
    height: number
    // 帧率 (计算分数形式的帧率)
    fps: number
}> => {
    const ffmpeg = await getFFmpeg()
    const inputFileName = await writeFile(inputFile)
    const infoFile = getFileName('info.json')
    await run(
        `ffprobe -v quiet -print_format json -show_format -show_streams ${inputFileName} -o ${infoFile}`,
    )
    const dataStr = await ffmpeg.readFile(infoFile, 'utf8')
    await ffmpeg.deleteFile(inputFileName)
    await ffmpeg.deleteFile(infoFile)
    const data = JSON.parse(dataStr as string)
    const videoStream = data?.streams?.find(
        (item: any) => item.codec_type === 'video',
    )
    if (!videoStream) {
        return Promise.reject('novideo')
    }
    return {
        fileName: inputFile.name,
        videoCodec: videoStream?.codec_name || '',
        bitrate: Math.ceil(Number(videoStream?.bit_rate)) || 0,
        duration: Math.ceil(Number(videoStream?.duration)) || 0,
        width: videoStream?.width || 0,
        height: videoStream?.height || 0,
        fps: calculateFraction(videoStream.avg_frame_rate),
    }
}

/**
 * @description: 获取视频每秒缩略图
 * @param {File} file
 * @return {*}
 */
export const getVideoShot = async (
    file: File,
): Promise<{
    // 封面图
    coverUrl: string
    // 预览动图
    previewUrl: string
}> => {
    const ffmpeg = await getFFmpeg()
    const inputFileName = await writeFile(file)
    // 构建输出文件名模式
    const thumbMode = 'thumb_%04d.jpg'
    const outputPattern = getFileName(thumbMode)
    const preStr = outputPattern.replace(thumbMode, '')
    await run(
        `ffmpeg -i ${inputFileName} -vf fps=1,scale=160:-1 -q:v 20 ${outputPattern}`,
    )
    const files = await ffmpeg.listDir('/')
    const thumbnailFiles = files
        .filter(
            (file) =>
                file.name.startsWith(preStr) && file.name.endsWith('.jpg'),
        )
        .sort((a, b) => a.name.localeCompare(b.name))
    // 输出文件名
    const outputFileName = getFileName('output.gif')
    await run(
        `ffmpeg -r 3 -i ${outputPattern} -f gif -gifflags +transdiff -y ${outputFileName}`,
    )
    const gifData = await ffmpeg.readFile(outputFileName)
    const gifBlob = new Blob([gifData], { type: 'image/gif' })
    const data = await ffmpeg.readFile(thumbnailFiles[0].name)
    const blob = new Blob([data], { type: 'image/jpg' })
    const [coverUrl, previewUrl] = await Promise.all([
        assetsUpload({ blob: blob, fileType: 'jpg' }),
        assetsUpload({ blob: gifBlob, fileType: 'gif' }),
    ])
    for (const thumbFile of thumbnailFiles) {
        await ffmpeg.deleteFile(thumbFile.name)
    }
    await ffmpeg.deleteFile(inputFileName)
    await ffmpeg.deleteFile(outputFileName)
    return {
        coverUrl,
        previewUrl,
    }
}
